import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { api } from '../api/client';
import { StatusBadge } from '../components/StatusBadge';
import { formatUzs } from '../lib/format';
import { toast } from '../stores/toasts';

interface PassportData {
  car: {
    id: string;
    plate: string;
    brand: string;
    model: string;
    year: number | null;
    color: string | null;
    vin: string | null;
  };
  owner?: { name: string; phone: string };
  orders: Array<{
    id: string;
    status: any;
    problem: string;
    receivedAt: string;
    deliveredAt: string | null;
    totalCached: number;
    mileageAtIntake: number | null;
    workshop: { id: string; name: string; city: string | null };
  }>;
  totalSpent: number;
  visits: number;
}

export function VehiclePassportPage() {
  const [params, setParams] = useSearchParams();
  const [plate, setPlate] = useState(params.get('plate') ?? '');
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    const p = params.get('plate');
    if (p) setPlate(p);
  }, [params]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['car-passport', plate],
    queryFn: () => api.getCarPassport(plate.trim().toUpperCase()),
    enabled: !!plate.trim(),
    retry: false,
  });

  function search() {
    if (!plate.trim()) {
      toast.warning('Davlat raqamini kiriting');
      return;
    }
    setParams({ plate: plate.trim().toUpperCase() });
  }

  const passport = data as PassportData | null | undefined;
  const recent = (passport?.orders ?? []).slice(0, 5);
  const olderCount = (passport?.orders?.length ?? 0) - recent.length;

  return (
    <>
      <h3>📜 Avtomobil pasporti</h3>
      <div className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
        Barcha servislardagi ta'mir tarixini ko'rish
      </div>
      <div className="row" style={{ gap: 8, marginBottom: 14 }}>
        <input
          placeholder="01A123BC"
          value={plate}
          onChange={(e) => setPlate(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && search()}
          style={{ flex: 1 }}
        />
        <button className="btn btn-primary" onClick={search}>🔍</button>
      </div>

      {isLoading ? (
        <div className="spinner" />
      ) : isError ? (
        <div className="empty">
          <div className="emoji">🔍</div>
          <div>Mashina topilmadi</div>
          <div className="muted" style={{ marginTop: 4 }}>
            {(error as any)?.message ?? 'Davlat raqamini tekshiring'}
          </div>
        </div>
      ) : passport ? (
        <>
          <div className="card" style={{ background: 'linear-gradient(135deg, var(--surface), var(--surface-2))' }}>
            <div className="row-between" style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 24, fontWeight: 700 }}>
                🚗 {passport.car.plate}
              </div>
            </div>
            <div style={{ fontSize: 14 }}>
              {passport.car.brand} {passport.car.model}
              {passport.car.year ? ` · ${passport.car.year}` : ''}
              {passport.car.color ? ` · ${passport.car.color}` : ''}
            </div>
            {passport.car.vin && (
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                VIN: {passport.car.vin}
              </div>
            )}
            {passport.owner && (
              <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                👤 Egasi: {passport.owner.name} · {passport.owner.phone}
              </div>
            )}
          </div>

          <div className="stat-grid" style={{ marginBottom: 14 }}>
            <div className="stat-card revenue">
              <div className="stat-label">Jami xarajat</div>
              <div className="stat-value amount" style={{ fontSize: 18 }}>{formatUzs(passport.totalSpent)}</div>
            </div>
            <div className="stat-card inProgress">
              <div className="stat-label">Tashriflar</div>
              <div className="stat-value">{passport.visits}</div>
              <div className="stat-sub">
                O'rtacha: {passport.visits > 0 ? formatUzs(passport.totalSpent / passport.visits) : '—'}
              </div>
            </div>
          </div>

          <div className="row-between" style={{ marginBottom: 8 }}>
            <h3 style={{ margin: 0 }}>📋 Ta'mir tarixi</h3>
            <span className="muted" style={{ fontSize: 12 }}>{passport.orders.length} ta</span>
          </div>

          {passport.orders.length > 0 ? (
            <>
              {recent.map((o) => (
                <div key={o.id} className="card">
                  <div className="row-between" style={{ marginBottom: 4 }}>
                    <div style={{ fontWeight: 600 }}>{o.workshop.name}</div>
                    <StatusBadge status={o.status} />
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
                    📍 {o.workshop.city ?? '—'} · {format(parseISO(o.receivedAt), 'dd MMM yyyy')}
                  </div>
                  <div style={{ fontSize: 13, marginBottom: 6 }}>{o.problem}</div>
                  <div className="row-between">
                    <span className="muted" style={{ fontSize: 11 }}>
                      {o.mileageAtIntake ? `${o.mileageAtIntake.toLocaleString('uz-UZ')} km` : '—'}
                    </span>
                    <span className="amount" style={{ fontWeight: 600 }}>{formatUzs(o.totalCached)}</span>
                  </div>
                </div>
              ))}
              {olderCount > 0 && (
                <button
                  className="btn btn-block"
                  onClick={() => setExpanded(expanded === 'all' ? null : 'all')}
                  style={{ marginTop: 4 }}
                >
                  {expanded === 'all' ? 'Yashirish' : `Yana ${olderCount} ta ko'rish`}
                </button>
              )}
              {expanded === 'all' && passport.orders.slice(5).map((o) => (
                <div key={o.id} className="card">
                  <div className="row-between" style={{ marginBottom: 4 }}>
                    <div style={{ fontWeight: 600 }}>{o.workshop.name}</div>
                    <StatusBadge status={o.status} />
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
                    📍 {o.workshop.city ?? '—'} · {format(parseISO(o.receivedAt), 'dd MMM yyyy')}
                  </div>
                  <div style={{ fontSize: 13, marginBottom: 6 }}>{o.problem}</div>
                  <div className="row-between">
                    <span className="muted" style={{ fontSize: 11 }}>
                      {o.mileageAtIntake ? `${o.mileageAtIntake.toLocaleString('uz-UZ')} km` : '—'}
                    </span>
                    <span className="amount" style={{ fontWeight: 600 }}>{formatUzs(o.totalCached)}</span>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="empty">
              <div className="emoji">📭</div>
              <div>Tarix bo'sh</div>
            </div>
          )}
        </>
      ) : null}
    </>
  );
}
